import { z } from 'zod'
import {
  EVALUATION_STATUSES,
  EVALUATOR_TYPES,
  SKILL_SCORE_MAX,
  SKILL_SCORE_MIN,
} from '../constants/skill-check'
import { FOLLOW_UP_STATUSES } from '../constants/training-enums'
import { DateOrStringSchema } from './common'

// ─── リクエスト ───────────────────────────────────────────

const scoreRangeMessage = `評価は${SKILL_SCORE_MIN}〜${SKILL_SCORE_MAX}の整数で入力してください。`

export const EvaluatorTypeSchema = z.enum(EVALUATOR_TYPES)

export const SkillScoreSchema = z
  .number()
  .int({ message: scoreRangeMessage })
  .min(SKILL_SCORE_MIN, { message: scoreRangeMessage })
  .max(SKILL_SCORE_MAX, { message: scoreRangeMessage })

export const SubmitEvaluationSchema = z.object({
  scores: z
    .array(
      z.object({
        itemCode: z.string().trim().min(1, { message: '項目コードが指定されていません。' }),
        score: SkillScoreSchema,
      }),
    )
    .min(1, { message: '評価を入力してください。' })
    .refine((scores) => new Set(scores.map((s) => s.itemCode)).size === scores.length, {
      message: '同じ項目が重複しています。',
    }),
})

export const ReturnEvaluationSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, { message: '差し戻し理由を入力してください。' })
    .max(1000, { message: '差し戻し理由は1000文字以内で入力してください。' }),
})

/** 人事による上司評価者の個別指定 */
export const AssignSupervisorSchema = z.object({
  userId: z.string().uuid({ message: '上司評価者を選択してください。' }),
})

export const AddFollowUpCommentSchema = z.object({
  content: z
    .string()
    .min(1, { message: 'コメントを入力してください。' })
    .max(5000, { message: 'コメントは5000文字以内で入力してください。' }),
})

export type SubmitEvaluationInput = z.infer<typeof SubmitEvaluationSchema>
export type ReturnEvaluationInput = z.infer<typeof ReturnEvaluationSchema>
export type AssignSupervisorInput = z.infer<typeof AssignSupervisorSchema>
export type AddFollowUpCommentInput = z.infer<typeof AddFollowUpCommentSchema>

// ─── レスポンス ───────────────────────────────────────────

export const FollowUpSkillItemResponseSchema = z.object({
  code: z.string(),
  category: z.string(),
  label: z.string(),
  sortOrder: z.number(),
})

export const EvaluationScoreResponseSchema = z.object({
  itemCode: z.string(),
  score: z.number(),
})

export const FollowUpEvaluationResponseSchema = z.object({
  evaluatorType: EvaluatorTypeSchema,
  status: z.enum(EVALUATION_STATUSES),
  evaluatorName: z.string().nullable(),
  submittedAt: DateOrStringSchema,
  returnedAt: DateOrStringSchema.nullable(),
  returnReason: z.string().nullable(),
  /** 閲覧者に閲覧権限がない場合は null */
  scores: z.array(EvaluationScoreResponseSchema).nullable(),
})

export const FollowUpCommentResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  userName: z.string(),
  content: z.string(),
  isSupervisor: z.boolean(),
  createdAt: DateOrStringSchema,
})

/** 閲覧者がこの回で実行できる操作（判定は backend に一元化し、frontend は表示の出し分けだけに使う） */
export const FollowUpPhaseActionsSchema = z.object({
  canSubmitSelf: z.boolean(),
  canSubmitSupervisor: z.boolean(),
  canReturnSelf: z.boolean(),
  canReturnSupervisor: z.boolean(),
  canComment: z.boolean(),
})

export const FollowUpPhaseResponseSchema = z.object({
  id: z.string(),
  phase: z.number(),
  label: z.string(),
  status: z.enum(FOLLOW_UP_STATUSES),
  availableFrom: DateOrStringSchema,
  dueDate: DateOrStringSchema,
  evaluations: z.object({
    self: FollowUpEvaluationResponseSchema.nullable(),
    supervisor: FollowUpEvaluationResponseSchema.nullable(),
  }),
  comments: z.array(FollowUpCommentResponseSchema),
  actions: FollowUpPhaseActionsSchema,
})

export const FollowUpSheetResponseSchema = z.object({
  enrollment: z.object({
    id: z.string(),
    status: z.string(),
    trainingTitle: z.string().nullable(),
    userName: z.string().nullable(),
  }),
  viewer: z.object({
    isTrainee: z.boolean(),
    isSupervisor: z.boolean(),
    isAdmin: z.boolean(),
  }),
  /** 研修ごとの補足（評価の目安など） */
  skillCheckNote: z.string().nullable(),
  /** 上司評価者（isAssigned = 人事による個別指定。未設定なら null） */
  supervisor: z
    .object({ userId: z.string(), name: z.string(), isAssigned: z.boolean() })
    .nullable(),
  skillItems: z.array(FollowUpSkillItemResponseSchema),
  phases: z.array(FollowUpPhaseResponseSchema),
})

export type FollowUpSkillItemResponse = z.infer<typeof FollowUpSkillItemResponseSchema>
export type FollowUpEvaluationResponse = z.infer<typeof FollowUpEvaluationResponseSchema>
export type FollowUpCommentResponse = z.infer<typeof FollowUpCommentResponseSchema>
export type FollowUpPhaseActions = z.infer<typeof FollowUpPhaseActionsSchema>
export type FollowUpPhaseResponse = z.infer<typeof FollowUpPhaseResponseSchema>
export type FollowUpSheetResponse = z.infer<typeof FollowUpSheetResponseSchema>
